/**
 * Widget → BRFrame chat postMessage 프로토콜 (v0.5 통일 envelope 카탈로그).
 *
 * 모든 outbound 메시지는 ``{source:"lge-commerce-widget", type:"<ns>.<verb>", payload}`` 형식이며,
 * BRFrame chat 은 ``<ns>`` (namespace) 만 보고 dispatch 한다:
 *
 *   - ``widget.*``   : 위젯 라이프사이클 (BRFrame chat 자체 처리, UI 인프라)
 *   - ``chat.*``     : BRFrame chat input/메시지 영역 조작 (자연어 등)
 *   - ``action.*``   : BRFrame chat 자체 액션 (login API 호출, 결제 redirect 등)
 *   - ``ncms.*``     : NCMS parent 로 단순 forward (BRFrame 은 해석하지 않음)
 *
 * SSE done.envelope 와 동일 envelope ({type, payload}) 를 사용한다.  Agent / Widget 어느
 * 쪽에서 발신해도 BRFrame chat 의 dispatch 함수가 동일하게 처리한다 (= "단일 명령 카탈로그").
 *
 * 정의: docs/cross-cutting/integration/FRONTEND_PROTOCOL.md
 *
 * Navigate URL 정책 (v3, CTA_NAVIGATE_GAP.md §0):
 * - **서버 단일 책임**.  모든 navigate URL 은 서버 (MCP/Agent) 에서 ``page`` origin 을
 *   포함한 full URL 로 완성되어 카드 payload 로 내려온다.
 * - 위젯은 transformation (host swap, locale 조립 등) **일체 금지**.  받은 URL 을
 *   그대로 ``navigateExternal`` 에 전달한다.
 * - 카드 컴포넌트 내부의 path 하드코딩 (예: ``"/cart"``) 도 금지 — payload 필드 사용.
 */

export type WidgetOutboundType =
  // ── 위젯 라이프사이클 (BRFrame chat 내부 처리, NCMS 비공개) ──
  | "widget.ready"              // iframe 부팅 완료 1회. payload: {component}
  | "widget.resize"             // 컨텐츠 높이 변동 보고. payload: {width, height}
  | "widget.error"              // 위젯 내부 오류. payload: {code, message}
  // ── chat input 조작 (자연어 prompt 입력) ──
  | "chat.send_prompt"          // payload: {message}  → BRFrame chat input 에 채우고 submit
  | "chat.toast"                // payload: {message}  → BRFrame chat 하단에 toast-popup full 표시 (2초 후 소멸)
  // ── BRFrame chat 자체 액션 ──
  | "action.submit_login"       // payload: {method, email, password}  → POST /api/commerce/auth/login
  | "action.open_external"      // payload: {url, target?} → BRFrame iframe 안에서 window.open(url, target||"_top")
  | "action.open_live_chat"     // payload: {} → BRFrame 이 PM-008 SHOP_LIVE_CHAT_OBS 를 NCMS 로 전달
  | "action.open_chat_summary_proposal" // payload: {sessionId?, variant?, locale?, defaultEmail?, labels} → BrFrame Proposal 모달
  // ── NCMS parent 로 forward (BRFrame 은 dumb relay) ──
  | "ncms.cart_sync"            // payload: {cart_id, subscribe_cart_id?}
  | "ncms.token_sync"           // payload: {d2x?, oms?, lge_cxr?}
  | "ncms.navigate_external"    // payload: {url, code?, cart_id?, target?}  → NCMS top-level location 전이
  // ── OBS Chat GA (KPI 원천) → BRFrame relay → NCMS dataLayer ──
  | "ga.custom_event";          // payload: custom_event_3rd_party flat (DEV_REQUEST_OBS_CHAT.md)
  // 채팅 iframe 툴바: chat.minimize / chat.close — Widget 발신 아님 (CHAT_TOOLBAR.md)

export interface WidgetMessage<T extends WidgetOutboundType = WidgetOutboundType> {
  source: "lge-commerce-widget";
  type: T;
  payload?: Record<string, unknown>;
  resultId?: string;
  sessionId?: string;
  ts: number;
}

const WIDGET_SOURCE = "lge-commerce-widget" as const;

export interface ToolCallDirective {
  tool?: string;
  scenario?: string;
  args?: Record<string, unknown>;
}

export function postToHost<T extends WidgetOutboundType>(
  type: T,
  payload?: Record<string, unknown>,
  meta?: { resultId?: string; sessionId?: string; toolCall?: ToolCallDirective }
) {
  const finalPayload = payload ? { ...payload } : undefined;
  if (meta?.toolCall && finalPayload) {
    finalPayload.toolCall = meta.toolCall;
  }
  const msg: WidgetMessage<T> = {
    source: WIDGET_SOURCE,
    type,
    payload: finalPayload,
    resultId: meta?.resultId,
    sessionId: meta?.sessionId,
    ts: Date.now(),
  };
  if (import.meta.env.DEV) {
    console.debug("[Widget→Host]", type, finalPayload ?? "");
  }
  window.parent?.postMessage(msg, "*");
}

/**
 * NCMS parent 로 top-level navigate 요청 발신.
 *
 * **url 은 반드시 full URL (scheme + host + locale + path) 이어야 한다.**
 * 서버 (MCP/Agent) 가 카드 payload 에 미리 완성해 내려준 값을 그대로 전달.
 * 상대 경로(``/cart``) 를 전달하지 말 것 — CTA_NAVIGATE_GAP.md §0 위반.
 */
export function navigateExternal(
  url: string,
  extra?: Record<string, unknown>,
  meta?: { resultId?: string; sessionId?: string },
  ga?: Record<string, unknown>,
) {
  if (!url || !/^https?:\/\//i.test(url)) {
    console.warn(
      "[Widget] navigateExternal: full URL expected (server should provide scheme+host+locale+path). " +
      "Got:", url,
    );
  }
  // B-5: 페이지 이동을 일으키는 클릭은 동반 GA 를 payload ``ga`` 에 임베드한다.
  // BRFrame(forwardEnvelope)이 GA(이동 전 flat)+navigate 2건으로 분리 relay 한다
  // (DEV_REQUEST_BRFRAME §7). 별도 ``ga.custom_event`` 를 추가로 쏘지 않는다(중복 방지).
  const payload: Record<string, unknown> = { url, ...extra };
  if (ga) payload.ga = ga;
  postToHost("ncms.navigate_external", payload, meta);
}

// ── OBS Chat GA 이벤트 발신 (KPI 원천) ───────────────────────────────────────
// 정본: docs/domain/kpi/DEV_REQUEST_OBS_CHAT.md · postMessage_guide.md
//
// Widget(nested iframe)은 NCMS 로 직접 postMessage 할 수 없으므로 ``ga.custom_event``
// envelope 로 BRFrame(parent)에 보낸다.  BRFrame 이 봉투를 벗겨 NCMS parent 로 flat
// ``custom_event_3rd_party`` 를 무변환 forward 한다 (DEV_REQUEST_BRFRAME §1, B-1).
//
// ``dynamic_param3`` = chat_session_id = Agent ``session_id``.  위젯 iframe URL 의
// ``sessionToken`` 쿼리(= build_widget_meta 가 주입한 session_id)에서 읽어 lifecycle
// 이벤트와 동일한 값을 보장한다 (R-2 단일 세션).

function gaChatSessionId(): string {
  try {
    return new URLSearchParams(window.location.search).get("sessionToken") || "";
  } catch {
    return "";
  }
}

/**
 * ``custom_event_3rd_party`` flat payload 를 조립한다(송신은 하지 않음).
 * 단독 ``ga.custom_event`` 발신과 navigate 임베드(B-5) 양쪽에서 재사용한다.
 */
export function gaCustomEventPayload(
  name: string,
  param4: string = "",
  param5: string = "",
): Record<string, string> {
  return {
    event: "custom_event_3rd_party",
    dynamic_param1: "ask_lg_chatbot",
    dynamic_param2: name,
    dynamic_param3: gaChatSessionId(),
    dynamic_param4: String(param4 ?? ""),
    dynamic_param5: String(param5 ?? ""),
    iframe_domain: window.location.origin,
  };
}

/**
 * ``custom_event_3rd_party`` flat payload 를 만들어 BRFrame 로 송신한다.
 * @param name    이벤트명 (``asklg_*``) → dynamic_param2
 * @param param4  dynamic_param4 (이벤트별 변동값)
 * @param param5  dynamic_param5 (이벤트별 변동값)
 */
export function sendGaEvent(name: string, param4: string = "", param5: string = "") {
  postToHost("ga.custom_event", gaCustomEventPayload(name, param4, param5));
}

/** 제품 카드 리스트 노출 — ``asklg_shop_product_card_list`` (SKU pipe + count). */
export function gaProductCardList(skus: Array<string | undefined>) {
  const clean = skus.filter((s): s is string => Boolean(s));
  if (clean.length === 0) return;
  // BrFrame 새로고침·위젯 iframe 재마운트 시 동일 resultId 중복 발화 방지.
  // resultId 가 발화 단위(노출된 카드 리스트 1건)이므로 resultId 있을 때만 dedup.
  try {
    const params = new URLSearchParams(window.location.search);
    const resultId = params.get("resultId") || "";
    if (resultId) {
      const dedupeKey = `obs_ga:pcl:${params.get("sessionToken") || ""}:${resultId}`;
      if (sessionStorage.getItem(dedupeKey)) return;
      sessionStorage.setItem(dedupeKey, "1");
    }
  } catch {
    /* sessionStorage 불가 환경 — 중복 허용 */
  }
  sendGaEvent("asklg_shop_product_card_list", clean.join("|"), String(clean.length));
}

/** 제품 카드 PDP 진입 클릭(Learn More/썸네일/타이틀) — ``asklg_shop_product_card_click``. */
export function gaProductCardClick(sku: string, isAnchor?: boolean) {
  if (!sku) return;
  sendGaEvent("asklg_shop_product_card_click", sku, isAnchor ? "y" : "n");
}

/**
 * B-5-2 — PDP 진입(페이지 이동)에 임베드할 ``asklg_shop_product_card_click`` payload.
 * 페이지 이동과 1쌍이므로 별도 ``ga.custom_event`` 대신 ``navigateExternal(…, ga)`` 로 운반한다.
 */
export function gaProductCardClickPayload(sku: string, isAnchor?: boolean) {
  return gaCustomEventPayload("asklg_shop_product_card_click", sku, isAnchor ? "y" : "n");
}

/**
 * B-5-1 — 링크아웃 CTA(예: Go to Basket) 클릭에 임베드할 ``asklg_shop_next_btn_click`` payload.
 * param4 = CTA 라벨(= suggestion_label 의미).
 */
export function gaCtaClickPayload(label: string) {
  return gaCustomEventPayload("asklg_shop_next_btn_click", label || "");
}

// asklg_shop_add_to_cart_click 는 위젯에서 직접 발화하지 않는다.
// 카드 버튼 클릭은 commerce_add_to_cart 도구 호출(chat.send_prompt)로 이어지고,
// 백엔드가 담기 **성공** 시 done.envelope 로 발화한다(말로 담기와 동일 KPI).

/**
 * 챗창 하단에 NCMS 공통 토스트 팝업(toast-popup full) 표시 요청.
 * 위젯은 토스트 DOM 을 직접 렌더링하지 않고, BRFrame chat 에 위임한다.
 */
export function postToastMessage(
  message: string,
  meta?: { sessionId?: string },
) {
  postToHost("chat.toast", { message }, meta);
}

/**
 * Live Chat 연결 — v0.5 envelope 로 BRFrame 에 전달.
 *
 * BRFrame 이 ``action.open_live_chat`` 를 수신하면 PM-008
 * ``SHOP_LIVE_CHAT_OBS`` 레거시 메시지를 NCMS parent 로 전달한다.
 * req.md §1-3 / BRFrame_NCMS_AgentGW_연동스펙_v1.6.xlsx PM-008 참조.
 */
export function postLiveChatEvent() {
  postToHost("action.open_live_chat", {});
}

/** Ask LG Proposal 모달 오픈 — BrFrame CS-1 (Scenario/Re-Entry 위젯 CTA).
 *
 * ConfirmationCard 가 tool/args 를 payload 로 넘기듯, 모달 카피(`labels`)를
 * host 가 그대로 렌더하도록 전달한다 (host 가 SSE 캐시·별도 i18n 조회 불필요).
 */
export function postOpenChatSummaryProposal(payload?: {
  sessionId?: string | null;
  variant?: string;
  locale?: string | null;
  /** 로그인 시 계정 이메일, 게스트는 "" — BrFrame guest/logged-in 모달 분기 */
  defaultEmail?: string | null;
  labels?: Record<string, string> | null;
}) {
  const sessionId = String(payload?.sessionId || "").trim();
  // focusFamily 는 생략 — summary-email / dismiss 가 세션에서 해석
  postToHost(
    "action.open_chat_summary_proposal",
    {
      sessionId: sessionId || null,
      variant: payload?.variant || "scenario",
      locale: payload?.locale ?? null,
      defaultEmail: String(payload?.defaultEmail || "").trim(),
      labels: payload?.labels && typeof payload.labels === "object" ? payload.labels : {},
    },
    sessionId ? { sessionId } : undefined,
  );
}

/**
 * ResizeObserver로 contentHeight를 부모 host에 보고.
 * iframe을 자식 콘텐츠 높이에 맞춰 자동 확장.
 *
 * scrollHeight 를 사용하여 CSS min-height / 100vh 등에 의한 팽창 없이
 * 실제 콘텐츠 높이를 측정한다.
 */
export function reportSize(
  el: HTMLElement,
  meta?: { resultId?: string; sessionId?: string }
): () => void {
  let prevH = -1;
  let prevW = -1;
  let rafId = 0;
  const send = () => {
    rafId = 0;
    const rect = el.getBoundingClientRect();
    const width = Math.ceil(rect.width);
    // 첫 자식의 상단 margin 이 el(.ac-shell, BFC 아님) 밖으로 상쇄되면 iframe 최상단
    // 여백이 되어 scrollHeight 에 안 잡힌다 → 호스트 iframe 이 그만큼 짧아져 하단이 잘린다
    // (AISASSIT-54). el 상단 위치(상쇄된 여백)를 더해 실제 점유 높이를 보고한다.
    const topGap = Math.max(0, Math.round(rect.top + window.scrollY));
    const height = Math.ceil(el.scrollHeight) + topGap;
    const widthChanged = Math.abs(width - prevW) > 1;
    const heightChanged = Math.abs(height - prevH) > 1;
    if (!widthChanged && !heightChanged) return;
    prevW = width;
    prevH = height;
    postToHost("widget.resize", { width, height }, meta);
  };

  const scheduleSend = () => {
    if (rafId) return;
    rafId = window.requestAnimationFrame(send);
  };

  send();
  const ro = new ResizeObserver(scheduleSend);
  ro.observe(el);
  return () => {
    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = 0;
    }
    ro.disconnect();
  };
}
